import { useP4ToastController } from '../../views/system/NotificationsArea';
import React, { useCallback, useState } from 'react';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useMutation } from '@apollo/client';
import { Customer, CustomerAccountStatus } from '../../graphql/entities';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import FluentModal from '../../components/modals/FluentModal';
import { BasicHeader } from '../BasicHeader';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import { DangerPill } from '../../components/StatusPill';
import { Description, Option } from '../../components/DropdownEditor/CustomOptions';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { ToastTrigger } from '@fluentui/react-components';
import { SecondaryButton, SuccessButton } from '../../components/connectedForm/Button';
import {
  AlertType,
  ICustomer,
  ICustomerAccountStatus,
  ICustomerAccountStatusInput,
  ICustomerMutationResult,
  ICustomerMutationVariables,
  Maybe,
} from '../../graphql/schema/p4types';
import { useHistory } from 'react-router';
import { SaveRegular } from '@fluentui/react-icons';

const CustomerAccountStatusUpdateModal = ({ customer }: { customer: ICustomer }) => {
  const { dispatchSimpleToast, dismissToast } = useP4ToastController();
  const history = useHistory();
  const [accountStatus, setAccountStatus] = useState<Maybe<ICustomerAccountStatus>>(customer.customerAccountStatus);

  const handleQueryError = useComponentQueryErrorHandling();
  const [customerUpdate] = useMutation<ICustomerMutationResult, ICustomerMutationVariables>(Customer.update!, {
    onError: handleQueryError,
  });

  const [saveInProgress, setSaveInProgress] = useState(false);

  const dismiss = useCallback(() => {
    dismissToast(`status-update-${customer.uuid}`);
  }, [customer.uuid, dismissToast]);

  const onClickUpdate = useCallback(() => {
    setSaveInProgress(true);
    customerUpdate({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          customer: {
            id: customer.id,
            uuid: customer.uuid,
            concurrencyVersion: customer.concurrencyVersion,
            customerAccountStatus: normalizeVariables<
              Maybe<ICustomerAccountStatus>,
              Maybe<ICustomerAccountStatusInput>
            >(accountStatus),
          },
        },
      },
    })
      .then(() => {
        dispatchSimpleToast('Customer Update', 'Customer account status has been updated', AlertType.Success);
      })
      .finally(() => {
        setSaveInProgress(false);
        dismiss();
      });
  }, [
    accountStatus,
    customer.concurrencyVersion,
    customer.id,
    customer.uuid,
    customerUpdate,
    dismiss,
    dispatchSimpleToast,
  ]);

  const onClickGoToCustomer = useCallback(() => {
    history.push({
      pathname: `${Customer.formUrl}${customer?.id}`,
      state: { previousLocation: history.location?.pathname || 'back' },
    });
    dismiss();
  }, [customer?.id, dismiss, history]);

  return (
    <FluentModal isOpen={true}>
      <BasicHeader secondaryTitle={customer.reference}>Update Account Status for {customer.name}</BasicHeader>
      <div style={{ padding: '1em' }}>
        <QueryDropdown
          {...CustomerAccountStatus.dropdown}
          label="Account Status"
          value={accountStatus}
          onChange={setAccountStatus}
          data-testid="customer-account-status"
          optionRenderer={({ option }: { option: ICustomerAccountStatus }) => {
            const onStopPill = option.onStop ? <DangerPill>On Stop</DangerPill> : null;
            return (
              <Option>
                {option.description}
                <Description>{onStopPill}</Description>
              </Option>
            );
          }}
        />
      </div>
      <ModalBottomButtonContainer>
        <SecondaryButton
          split={true}
          menuProps={{ items: [{ key: 'viewCustomer', text: 'Skip & View Customer', onClick: onClickGoToCustomer }] }}
          onClick={() => {
            dismissToast(`status-update-${customer.uuid}`);
          }}
          disabled={saveInProgress}
        >
          Skip
        </SecondaryButton>
        <SuccessButton
          data-testid="customer-account-status-update-button"
          onClick={onClickUpdate}
          iconProps={{ children: <SaveRegular /> }}
          disabled={saveInProgress}
        >
          Save
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

/**
 * Returns a function that takes a Customer argument and uses it to generate a toast-driven prompt to
 * update that Customers' Account Status.
 */
export const useCustomerAccountStatusUpdate = () => {
  const { dispatchToast } = useP4ToastController();

  return (customer: ICustomer) => {
    // Dispatch a Custom Toast to present the account status update so that we can't interrupt it with navigation.
    dispatchToast(<CustomerAccountStatusUpdateModal customer={customer} />, {
      title: 'Account Status Update',
      message: 'Customer account status update prompted',
      type: AlertType.Success,
      intent: 'success',
      timeout: -1,
      toastId: `status-update-${customer.uuid}`,
    });
  };
};
